import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, FileDown, ImageDown, Maximize2, Wallet } from "lucide-react";
import { API, api, errMsg, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CardVisual, eurC, slvx } from "./CardVisual";

export const TX_LABEL = { ricarica_admin: "Ricarica amministrativa", bonus: "Bonus promozionale", utilizzo: "Utilizzo credito", rimborso_interno: "Rimborso interno", rettifica: "Rettifica amministrativa", ricarica_stripe: "Ricarica con carta (Stripe)" };

export function TxList({ items }) {
  if (!items.length) return <p className="text-xs text-slate-500" data-testid="wallet-no-tx">Nessun movimento.</p>;
  return (
    <ul className="divide-y divide-slate-100" data-testid="wallet-tx-list">
      {items.map((t) => (
        <li key={t.id} className="flex items-center justify-between gap-3 py-2 text-xs" data-testid={`wallet-tx-${t.id}`}>
          <span><b className="text-slate-800">{TX_LABEL[t.transaction_type]}</b><br /><span className="text-slate-500">{fmtDateTime(t.created_at)} · {t.description}</span></span>
          <span className={`font-mono font-bold ${t.amount_cents >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{t.amount_cents >= 0 ? "+" : ""}{slvx(t.amount_cents)}</span>
        </li>
      ))}
    </ul>
  );
}

function TopupDialog({ d, onClose }) {
  const [amount, setAmount] = useState("25");
  const [busy, setBusy] = useState(false);
  const tokens = Number(String(amount).replace(",", ".")) * 100 / d.eur_cents_per_slvx;
  const pay = () => {
    setBusy(true);
    api.post("/wallet/me/topup-checkout", { amount, origin_url: window.location.origin })
      .then(({ data }) => { window.location.href = data.url; }).catch((e) => { toast.error(errMsg(e)); setBusy(false); });
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="wallet-topup-dialog">
        <DialogHeader><DialogTitle>Ricarica con Selvix Token</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p>Saldo attuale: <b data-testid="topup-balance">{slvx(d.wallet.balance_cents)}</b> · Wallet {d.wallet.public_wallet_id}</p>
          <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">1 Selvix Token = {eurC(d.eur_cents_per_slvx)}. Scegli quanto ricaricare (da 1 € a 1.000 €) e paga con carta sulla pagina sicura di Stripe (modalità di prova). SLVX è un credito interno, non convertibile né rimborsabile in euro.</p>
          <div className="flex flex-wrap gap-2">{["10", "25", "50", "100"].map((v) => <Button key={v} size="sm" variant={amount === v ? "default" : "outline"} onClick={() => setAmount(v)} data-testid={`topup-amount-${v}`}>{v} €</Button>)}</div>
          <Input data-testid="topup-amount-input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Importo in euro" />
          <p className="text-xs text-slate-600" data-testid="topup-tokens-preview">Riceverai <b>{Number.isFinite(tokens) && tokens > 0 ? slvx(Math.round(tokens * 100)) : "—"}</b></p>
          <Button data-testid="topup-pay-stripe" disabled={busy} onClick={pay} className="w-full rounded-full bg-cyan-600 hover:bg-cyan-700">{busy ? "Apertura pagamento…" : `Paga ${amount || 0} € con carta`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function QrFull({ src, onClose }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="wallet-qr-full">
        <DialogHeader><DialogTitle>QR del Selvix Wallet</DialogTitle></DialogHeader>
        <img src={src} alt="QR del Selvix Wallet" className="w-full rounded-xl bg-white p-2" />
        <p className="text-xs text-slate-500">Il QR identifica la carta: non autorizza pagamenti o trasferimenti.</p>
      </DialogContent>
    </Dialog>
  );
}

function checkReturn(reload) {
  const qs = new URLSearchParams(window.location.search);
  const sid = qs.get("wallet_session");
  if (qs.get("wallet_annullato")) toast.info("Pagamento annullato: nessun addebito");
  if (qs.has("wallet_session") || qs.has("wallet_annullato")) window.history.replaceState({}, "", window.location.pathname);
  if (!sid) return;
  let n = 0;
  const poll = () => api.get("/wallet/me/topup-status", { params: { session_id: sid } }).then(({ data }) => {
    if (data.wallet_credit === "accreditato") { toast.success(`Ricarica completata: +${data.amount} SLVX`); reload(); }
    else if (data.wallet_credit === "fallito") toast.error("Pagamento ricevuto ma accredito non riuscito: il Super Admin è stato avvisato");
    else if (data.payment_status === "expired") toast.error("Sessione di pagamento scaduta");
    else if (++n < 8) setTimeout(poll, 2000);
    else toast.info("Pagamento in verifica: il saldo si aggiornerà a breve");
  }).catch((e) => toast.error(errMsg(e)));
  poll();
}

export function MyCard() {
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState("");
  const load = () => api.get("/wallet/me").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { checkReturn(load); }, []);
  if (!d) return <div className="h-48 animate-pulse rounded-3xl bg-slate-100" />;
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]" data-testid="my-card-section">
      <div className="space-y-3">
        <h2 className="font-display text-lg font-bold text-slate-900">La mia DoctorSelvix Card</h2>
        <CardVisual card={d.card} name={d.holder.name} onQr={() => setDlg("qr")} />
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="outline"><a href={`${API}/wallet/me/card.png`} data-testid="card-download-png"><ImageDown className="mr-1 h-4 w-4" />Scarica PNG</a></Button>
          <Button asChild size="sm" variant="outline"><a href={`${API}/wallet/me/card.pdf`} data-testid="card-download-pdf"><FileDown className="mr-1 h-4 w-4" />Scarica PDF</a></Button>
          <Button size="sm" variant="outline" onClick={() => setDlg("qr")} data-testid="card-qr-full-btn"><Maximize2 className="mr-1 h-4 w-4" />QR a schermo intero</Button>
        </div>
      </div>
      <div className="glass space-y-4 rounded-3xl p-5" data-testid="wallet-panel">
        <p className="flex items-center gap-2 text-sm font-bold text-slate-800"><Wallet className="h-4 w-4 text-cyan-700" />Selvix Wallet</p>
        <div><p className="font-display text-3xl font-extrabold text-slate-900" data-testid="wallet-balance">{slvx(d.wallet.balance_cents)}</p>
          <p className="text-xs text-slate-500" data-testid="wallet-eur">Valore di riferimento {eurC(d.eur_value_cents)} · credito interno non convertibile</p></div>
        <dl className="grid grid-cols-2 gap-2 text-xs">
          <div><dt className="text-slate-500">N° tessera</dt><dd className="font-mono font-bold" data-testid="wallet-card-number">{d.card.card_number}</dd></div>
          <div><dt className="text-slate-500">N° wallet</dt><dd className="font-mono font-bold" data-testid="wallet-id">{d.wallet.public_wallet_id}</dd></div>
          <div><dt className="text-slate-500">Emessa il</dt><dd>{fmtDateTime(d.card.issued_at)}</dd></div>
          <div><dt className="text-slate-500">Ultima ricarica</dt><dd data-testid="wallet-last-topup">{d.wallet.last_topup_at ? fmtDateTime(d.wallet.last_topup_at) : "—"}</dd></div>
        </dl>
        <Button data-testid="wallet-topup-btn" onClick={() => setDlg("topup")} className="w-full rounded-full bg-gradient-to-r from-cyan-600 to-sky-700 py-6 text-sm font-extrabold tracking-wide"><Coins className="mr-2 h-5 w-5" />RICARICA CARTA CON SELVIX TOKEN</Button>
        <div><p className="mb-1 text-xs font-bold text-slate-700">Ultime transazioni</p><TxList items={d.transactions} /></div>
      </div>
      {dlg === "qr" && <QrFull src={`${API}/wallet/me/qr.svg`} onClose={() => setDlg("")} />}
      {dlg === "topup" && <TopupDialog d={d} reload={load} onClose={() => setDlg("")} />}
    </div>
  );
}
